// ============================================================================
// TABLE COLUMNS
// ----------------------------------------------------------------------------
// Column set for the "Table" deadlines view. It reuses the interactive
// columns from `getTaskColumns` (checkbox, description, party, status,
// assignee, actions) but drops the file-icon `source` column and the standalone
// `date` column, replacing them with a single leftmost Date column that:
//   - renders the collapsed synthetic date (read-only; a click opens the
//     deadline side panel, where the date can be edited); and
//   - hosts the subtask expand chevron previously carried by the source column.
// Alternate sourced dates (different calendar days) are listed as "Possible
// dates" in the deadline detail panel, not in this column.
// ============================================================================
import { ColumnDef, flexRender } from '@tanstack/react-table';
import { parseISO } from 'date-fns';
import { ArrowUpDown, Circle, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useMatterLocale } from '@/components/contexts/MatterJurisdictionContext';
import { InlineDeadlineStatus } from '@/components/tasks/components/InlineDeadlineStatus';
import { OutlookSyncCell } from '@/components/tasks/components/OutlookSyncCell';
import type { OutlookCalendar } from '@/hooks/useOutlookCalendars';
import { getDocumentIcon } from '@/lib/document-icons';
import { cn } from '@/lib/utils';
import { formatRegionalDate, type RegionalDateLocale } from '@/utils/date-utils';
import { toLocalDate } from '@/utils/temporal';
import { Button } from './button';
import { ColumnHeaderFilter } from './data-table-filter-toolbar';
import {
  getTaskColumns,
  getTaskSourceDocuments,
  PartyCell,
  SourceDocumentLink,
  type SyntheticDateFieldValue,
  type Task,
  type TaskActionHandlers,
  type TaskSourceDocument,
} from './columns-tasks';
import { SingleDateCalendar } from './DatePicker';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './hover-card';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { customDateFilter } from './task-column-utils';

// ============================================================================
// DATE NORMALIZATION
// ----------------------------------------------------------------------------
// Synthetic deadlines expose date candidates in `field_values` (field_name ===
// 'date'), each carrying an EDTF `temporal_expression` plus derived
// `temporal_lower`/`temporal_upper` ISO bounds. Precision isn't surfaced by the
// backend yet, so single / date+time / range is inferred client-side from the
// bounds. See the TODO in `hooks/useDeadlines.ts`.
// ============================================================================

type DateDisplay =
  | { kind: 'multiple'; candidates: Array<{ label: string; reasoning?: string | null }> }
  | { kind: 'range'; lowerLabel: string; upperLabel: string; expression?: string | null }
  | { kind: 'datetime'; dateLabel: string; timeLabel: string; expression?: string | null }
  | { kind: 'single'; label: string; expression?: string | null }
  | { kind: 'trigger'; expression?: string | null }
  | { kind: 'fallback' };

function getDateCandidates(task: Task): SyntheticDateFieldValue[] {
  return (task.field_values ?? []).filter((fv) => fv.field_name === 'date');
}

// Calendar-day key (YYYY-MM-DD) for a date candidate, else null.
function candidateDayKey(fv: SyntheticDateFieldValue): string | null {
  if (fv.temporal_lower) return fv.temporal_lower.split('T')[0];
  if (fv.temporal_expression) return fv.temporal_expression.split('T')[0];
  if (fv.value != null) return String(fv.value).split('T')[0];
  return null;
}

// Tighter EDTF precision scores higher: day (or finer) = 3, month = 2, year = 1.
// The backend parses EDTF; here we approximate from the expression's shape,
// which is exact for the point YYYY-MM-DD candidates production writes today.
function datePrecisionScore(fv: SyntheticDateFieldValue): number {
  const expr = fv.temporal_expression;
  if (!expr) return 3;
  const start = expr.split('/')[0].replace(/[~?]$/, '').trim();
  const dashes = (start.split('T')[0].match(/-/g) ?? []).length;
  if (dashes >= 2) return 3; // YYYY-MM-DD (or finer)
  if (dashes === 1) return 2; // YYYY-MM
  return 1; // YYYY
}

// Source authority: docket (2) beats manual (1) beats extracted (0).
function sourceAuthority(fv: SyntheticDateFieldValue): number {
  if (fv.is_docket) return 2;
  if (fv.is_manual) return 1;
  return 0;
}

// Compare two rank tuples lexicographically; true when `a` outranks `b`.
function isRankGreater(a: Array<number | string>, b: Array<number | string>): boolean {
  for (let i = 0; i < a.length; i++) {
    if (a[i] === b[i]) continue;
    return a[i] > b[i];
  }
  return false;
}

// Mirrors backend `pick_winning_date_candidate`: user-edited, then source
// authority (docket > manual > extracted), then EDTF precision, then
// corroboration (how many candidates share the calendar day), then the latest
// `created_at`. Confidence is intentionally NOT part of the key. Equal ranks
// keep the first candidate (same as Python `max(..., key=rank)`).
function pickRepresentativeDateCandidate(
  candidates: SyntheticDateFieldValue[],
): SyntheticDateFieldValue | undefined {
  if (candidates.length === 0) return undefined;

  const dayCounts = new Map<string, number>();
  for (const fv of candidates) {
    const day = candidateDayKey(fv);
    if (day !== null) dayCounts.set(day, (dayCounts.get(day) ?? 0) + 1);
  }

  const rankOf = (fv: SyntheticDateFieldValue): Array<number | string> => {
    const day = candidateDayKey(fv);
    const corroboration = day !== null ? (dayCounts.get(day) ?? 1) : 1;
    return [
      fv.is_user_edited ? 1 : 0,
      sourceAuthority(fv),
      datePrecisionScore(fv),
      corroboration,
      fv.created_at ?? '',
    ];
  };

  let best = candidates[0];
  let bestRank = rankOf(best);
  for (let i = 1; i < candidates.length; i++) {
    const candidateRank = rankOf(candidates[i]);
    if (isRankGreater(candidateRank, bestRank)) {
      best = candidates[i];
      bestRank = candidateRank;
    }
  }
  return best;
}

// A time is meaningful only when the ISO string carries a non-midnight clock.
function hasTime(iso?: string | null): boolean {
  if (!iso) return false;
  const timePart = iso.split('T')[1];
  if (!timePart) return false;
  return !/^00:00(:00)?/.test(timePart);
}

// Format only the date portion (avoids timezone day-shift from the clock/offset).
function dayLabel(iso: string, locale: RegionalDateLocale): string {
  return formatRegionalDate(iso.split('T')[0], locale);
}

// Pull "HH:mm" straight from the ISO string rather than re-zoning it.
function timeLabel(iso: string): string {
  const timePart = iso.split('T')[1];
  return timePart ? timePart.slice(0, 5) : '';
}

function candidateLabel(candidate: SyntheticDateFieldValue, locale: RegionalDateLocale): string {
  if (candidate.temporal_lower) return dayLabel(candidate.temporal_lower, locale);
  if (candidate.temporal_expression) return candidate.temporal_expression;
  if (candidate.value != null) return String(candidate.value);
  return 'Date TBC';
}

/** Canonical single date the Table date column sorts and filters on.
 *  Picks the same candidate as the backend collapsed `date` (the evidence
 *  policy in `pickRepresentativeDateCandidate`: user-edited, then source
 *  authority, precision, corroboration, recency), not the earliest AI bound.
 *  Bounds are parsed as local midnight so UTC-midnight EDTF values don't
 *  shift a day west of UTC (unlike `parseISO`). Falls back to `task.date`. */
export function getDeadlineSortDate(task: Task): Date {
  const best = pickRepresentativeDateCandidate(getDateCandidates(task));
  const iso = best?.temporal_lower ?? best?.temporal_upper ?? null;
  if (iso) {
    const time = toLocalDate(iso).getTime();
    if (!Number.isNaN(time)) return new Date(time);
  }
  return toLocalDate(task.date);
}

// Two date candidates count as "the same date" when they resolve to the same
// calendar day. The cell only ever displays that day — `candidateLabel` renders
// `dayLabel(temporal_lower)`, ignoring time-of-day and the upper bound — so key
// on the day portion alone. Backfilled/old deadlines carry several candidates
// that show the same day but differ in timestamp or upper bound; keying on the
// full ISO (or bounds) left them as separate rows and produced a spurious
// "N possible dates" chip listing the same date repeatedly. For trigger /
// expression-only candidates with no bounds, fall back to the expression/value.
function dateCandidateKey(candidate: SyntheticDateFieldValue): string {
  if (candidate.temporal_lower) return `day:${candidate.temporal_lower.split('T')[0]}`;
  if (candidate.temporal_expression) return `expr:${candidate.temporal_expression}`;
  if (candidate.value != null) return `value:${String(candidate.value)}`;
  return 'tbc';
}

function distinctDateCandidates(
  candidates: SyntheticDateFieldValue[],
): SyntheticDateFieldValue[] {
  const seen = new Set<string>();
  const distinct: SyntheticDateFieldValue[] = [];
  for (const candidate of candidates) {
    const key = dateCandidateKey(candidate);
    if (seen.has(key)) continue;
    seen.add(key);
    distinct.push(candidate);
  }
  return distinct;
}

// Retained for a later pass: this is the richer multi-candidate / range /
// datetime / on-trigger date description. Currently unused while the table
// cell shows only the cached synthetic date.
function describeDeadlineDate(task: Task, locale: RegionalDateLocale): DateDisplay {
  const candidates = distinctDateCandidates(getDateCandidates(task));

  if (candidates.length >= 2) {
    return {
      kind: 'multiple',
      candidates: candidates.map((candidate) => ({
        label: candidateLabel(candidate, locale),
        reasoning: candidate.reasoning,
      })),
    };
  }

  if (candidates.length === 1) {
    const candidate = candidates[0];
    const { temporal_lower: lower, temporal_upper: upper, temporal_expression: expression } = candidate;

    if (!lower) {
      return { kind: 'trigger', expression };
    }

    if (upper) {
      const spanDays = (parseISO(upper).getTime() - parseISO(lower).getTime()) / 86_400_000;
      if (spanDays > 1) {
        return {
          kind: 'range',
          lowerLabel: dayLabel(lower, locale),
          upperLabel: dayLabel(upper, locale),
          expression,
        };
      }
    }

    if (hasTime(lower)) {
      return { kind: 'datetime', dateLabel: dayLabel(lower, locale), timeLabel: timeLabel(lower), expression };
    }

    return { kind: 'single', label: dayLabel(lower, locale), expression };
  }

  return { kind: 'fallback' };
}

// ============================================================================
// DATE CELL
// ============================================================================

// Editable single-date cell, used for legacy rows with no synthetic date
// candidates.
const EditableSingleDate = ({
  task,
  onUpdateDate,
}: {
  task: Task;
  onUpdateDate?: (taskId: string, date: Date) => void;
}) => {
  const locale = useMatterLocale();
  const [open, setOpen] = useState(false);

  const date = toLocalDate(task.date);
  const isValid = !isNaN(date.getTime());
  const label = isValid ? formatRegionalDate(date, locale) : 'Invalid date';

  if (!onUpdateDate) {
    return (
      <div className={cn('text-sm font-medium tabular-nums whitespace-nowrap', isValid ? 'text-gray-900' : 'text-red-600')}>
        {label}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'text-sm font-medium tabular-nums whitespace-nowrap rounded-sm px-2 py-1 text-left transition-colors hover:bg-gray-100',
            isValid ? 'text-gray-900' : 'text-red-600',
          )}
          title="Click to change due date"
        >
          {label}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <SingleDateCalendar
          value={isValid ? date : undefined}
          onChange={(next) => {
            if (next) {
              onUpdateDate(task.id, next);
            }
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export function sourceDocsForCandidate(
  task: Task,
  candidate: SyntheticDateFieldValue,
): TaskSourceDocument[] {
  const own = (candidate.source_documents ?? []).filter((source) => !!source.document_guid);
  if (own.length > 0) return own;
  const guid = candidate.source_event_guid;
  if (!guid) return [];
  return getTaskSourceDocuments(task).filter((source) => source.canonical_event_guid === guid);
}

function localDayKey(date: Date): string | null {
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Calendar day of a date candidate as a local-midnight `Date`, or null when
 *  the candidate has no parseable bound (trigger / expression-only). */
export function candidateToLocalDate(candidate: SyntheticDateFieldValue): Date | null {
  const iso = candidate.temporal_lower ?? candidate.temporal_upper ?? candidate.temporal_expression ?? null;
  if (!iso) return null;
  const date = toLocalDate(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function mergeUniqueSources(
  existing: TaskSourceDocument[],
  incoming: TaskSourceDocument[],
): TaskSourceDocument[] {
  const seen = new Set(
    existing.map((source) => source.document_guid ?? source.document_name ?? source.display_name ?? ''),
  );
  const merged = [...existing];
  for (const source of incoming) {
    const key = source.document_guid ?? source.document_name ?? source.display_name ?? '';
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    merged.push(source);
  }
  return merged;
}

export type AlternateDateOption = {
  candidate: SyntheticDateFieldValue;
  sources: TaskSourceDocument[];
};

/** Date candidates that disagree with the row's main due date. Same-day
 *  corroboration is hidden — that is not a real disagreement. Remaining
 *  candidates are distinct by calendar day, with source documents from every
 *  candidate on that day merged so the list can pair a date with its sources. */
export function getAlternateDateCandidates(task: Task): AlternateDateOption[] {
  const mainDay = localDayKey(toLocalDate(task.date));
  const byDay = new Map<string, AlternateDateOption>();
  for (const candidate of getDateCandidates(task)) {
    const day = candidateDayKey(candidate);
    if (day === mainDay) continue;
    const key = day ?? dateCandidateKey(candidate);
    const sources = sourceDocsForCandidate(task, candidate);
    const existing = byDay.get(key);
    if (!existing) {
      byDay.set(key, { candidate, sources });
    } else {
      existing.sources = mergeUniqueSources(existing.sources, sources);
    }
  }
  return Array.from(byDay.values());
}

function provenanceLabel(candidate: SyntheticDateFieldValue): string | null {
  if (candidate.is_user_edited) return 'Edited';
  if (candidate.is_docket) return 'Docket';
  if (candidate.is_manual) return 'Manual';
  return null;
}

const CANDIDATE_SOURCE_LINK_CLASS =
  'flex min-w-0 max-w-full items-center text-left text-sm text-gray-500 hover:text-gray-700 hover:underline underline-offset-2';

const DateCandidateRow = ({
  candidate,
  sources,
  task,
  locale,
  slug,
  handlers,
  onSelect,
}: {
  candidate: SyntheticDateFieldValue;
  sources: TaskSourceDocument[];
  task: Task;
  locale: RegionalDateLocale;
  slug?: string;
  handlers?: TaskActionHandlers;
  onSelect?: (date: Date) => void;
}) => {
  const date = candidateToLocalDate(candidate);
  const label = candidateLabel(candidate, locale);
  const fallback = provenanceLabel(candidate);
  const selectable = !!onSelect && !!date;

  const sourceColumn =
    sources.length > 0 && slug && handlers ? (
      <div className="flex min-w-0 flex-col">
        {sources.map((source, i) => (
          <SourceDocumentLink
            key={source.document_guid ?? i}
            source={source}
            task={task}
            slug={slug}
            handlers={handlers}
            showLabel
            hideIcon
            labelClassName={CANDIDATE_SOURCE_LINK_CLASS}
          />
        ))}
      </div>
    ) : sources.length > 0 ? (
      <span className="min-w-0 truncate text-sm text-gray-500">
        {sources[0].display_name || sources[0].document_name || sources[0].subject || 'Source'}
      </span>
    ) : fallback ? (
      <span className="text-sm text-gray-400">{fallback}</span>
    ) : null;

  return (
    <div className="group/alt -mx-1.5 flex items-start gap-2 rounded-md px-1.5 py-1.5 hover:bg-gray-50">
      {selectable ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (date && onSelect) onSelect(date);
          }}
          title={`Use ${label} as the due date`}
          aria-label={`Use ${label} as the due date`}
          className="flex shrink-0 items-start gap-2 rounded-sm text-left"
        >
          <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-300 group-hover/alt:text-gray-500" />
          <span className="w-28 text-sm tabular-nums whitespace-nowrap text-gray-700">{label}</span>
        </button>
      ) : (
        <>
          <span className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="w-28 shrink-0 text-sm tabular-nums whitespace-nowrap text-gray-700">{label}</span>
        </>
      )}
      <div className="min-w-0 flex-1 pt-px">{sourceColumn}</div>
    </div>
  );
};

const PossibleDatesList = ({
  task,
  slug,
  handlers,
  onUpdateDate,
}: {
  task: Task;
  slug?: string;
  handlers?: TaskActionHandlers;
  onUpdateDate?: (taskId: string, date: Date) => void;
}) => {
  const locale = useMatterLocale();
  const alternates = getAlternateDateCandidates(task);
  if (alternates.length === 0) return null;

  return (
    <div className="mt-3">
      <h5 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-gray-400">
        Also mentioned
      </h5>
      <div className="flex min-w-0 flex-col">
        {alternates.map(({ candidate, sources }, i) => (
          <DateCandidateRow
            key={candidate.id ?? `${candidate.source_event_guid ?? 'date'}-${i}`}
            candidate={candidate}
            sources={sources}
            task={task}
            locale={locale}
            slug={slug}
            handlers={handlers}
            onSelect={onUpdateDate ? (date) => onUpdateDate(task.id, date) : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export const DeadlineDueCell = ({
  task,
  onUpdateDate,
  slug,
  handlers,
  showPossibleDates = false,
}: {
  task: Task;
  onUpdateDate?: (taskId: string, date: Date) => void;
  slug?: string;
  handlers?: TaskActionHandlers;
  showPossibleDates?: boolean;
}) => {
  const main = <EditableSingleDate task={task} onUpdateDate={onUpdateDate} />;
  if (!showPossibleDates) return main;

  return (
    <div className="flex min-w-0 flex-col">
      {main}
      <PossibleDatesList
        task={task}
        slug={slug}
        handlers={handlers}
        onUpdateDate={onUpdateDate}
      />
    </div>
  );
};

// ============================================================================
// DATE COLUMN
// ============================================================================

export interface TableOutlookOptions {
  availableCalendars: OutlookCalendar[];
  calendarAvailable: boolean;
  onSyncToCalendar: (taskGuid: string, calendarId: string) => Promise<boolean>;
  onUnsyncFromCalendar?: (taskGuid: string, calendarId: string, myCalendarId?: string) => Promise<boolean>;
  onMenuClose?: () => void | Promise<unknown>;
  isSyncing?: boolean;
}

// Uses an `accessorFn` (id 'date') returning the ranked synthetic date so the
// date filter/sort follow the backend collapsed `date` (user-edited / highest
// confidence), not the earliest AI candidate. The default date-window quick
// filters (seeded in the page) target column id 'date' and keep working.
const buildDateColumn = (
  handlers: Parameters<typeof getTaskColumns>[0],
  activeFiltersRef: Parameters<typeof getTaskColumns>[2],
): ColumnDef<Task> => ({
  id: 'date',
  accessorFn: (row) => getDeadlineSortDate(row),
  meta: { filter: { type: 'date', label: 'Due Date', order: 4 } },
  size: 220,
  minSize: 200,
  header: ({ column, table }) => (
    <div className="flex flex-row items-center gap-0 pr-4">
      <ColumnHeaderFilter table={table} columnId="date" className="whitespace-nowrap">
        Date
      </ColumnHeaderFilter>
      <Button
        variant="ghost"
        size="sm"
        className="ml-2 w-6 h-8"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      >
        <ArrowUpDown className="h-4 w-4" strokeWidth={1.5} />
      </Button>
    </div>
  ),
  cell: ({ row }) => {
    const task = row.original;

    return (
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
        <DeadlineDueCell task={task} />
        <InlineDeadlineStatus
          state={task.state}
          createdBy={task.created_by}
          date={task.date}
          onMoveToNeedsConfirmation={() => handlers.onMoveToNeedsConfirmation(task.id)}
          onConfirm={() => handlers.onConfirm(task.id)}
          onComplete={() => handlers.onComplete(task.id)}
        />
      </div>
    );
  },
  filterFn: (row, columnId, filterValue) => {
    if (activeFiltersRef) {
      activeFiltersRef.current.date = filterValue;
    }
    return customDateFilter(row, columnId, filterValue);
  },
  sortingFn: (rowA, rowB) => {
    const dateA = getDeadlineSortDate(rowA.original).getTime();
    const dateB = getDeadlineSortDate(rowB.original).getTime();
    if (isNaN(dateA) || isNaN(dateB)) return 0;
    return dateA - dateB;
  },
  enableSorting: true,
});

const buildOutlookColumn = (
  outlook: TableOutlookOptions,
): ColumnDef<Task> => ({
  id: 'outlookSync',
  size: 72,
  minSize: 72,
  maxSize: 72,
  header: () => <span className="whitespace-nowrap">Synced</span>,
  cell: ({ row }) => (
    <OutlookSyncCell
      task={row.original}
      availableCalendars={outlook.availableCalendars}
      onSyncToCalendar={outlook.onSyncToCalendar}
      onUnsyncFromCalendar={outlook.onUnsyncFromCalendar}
      onMenuClose={outlook.onMenuClose}
      disabled={outlook.isSyncing}
    />
  ),
  enableSorting: false,
});

// ============================================================================
// SOURCES COLUMN
// ----------------------------------------------------------------------------
// A labelled "Sources" column for the Table, built from the real
// `task.source_documents` and rendered with the main app's document components
// (icon + link, "+N" popover for multiple). Exposes a file-type facet filter so
// the existing "File type" filter keeps working here.
// ============================================================================

// Auto-extracted rows are the ones that carry meaningful source documents;
// manually-created tasks (numeric `created_by`) don't.
const isAutoExtracted = (task: Task): boolean =>
  task.created_by === null || task.created_by === undefined || task.created_by === 'auto';

const SourcesCell = ({
  task,
  slug,
  handlers,
}: {
  task: Task;
  slug?: string;
  handlers: Parameters<typeof getTaskColumns>[0];
}) => {
  const sourceDocs = getTaskSourceDocuments(task);

  if (!slug || !isAutoExtracted(task) || sourceDocs.length === 0) {
    return <span className="text-[11px] text-gray-300">—</span>;
  }

  // Toned-down, icon-free label: small grey text, truncated to the column width.
  const labelClassName =
    'flex min-w-0 max-w-full items-center text-left text-[11px] text-gray-500 hover:text-gray-700 hover:underline underline-offset-2';

  if (sourceDocs.length === 1) {
    return (
      <SourceDocumentLink
        source={sourceDocs[0]}
        task={task}
        slug={slug}
        handlers={handlers}
        showLabel
        hideIcon
        labelClassName={labelClassName}
      />
    );
  }

  const primary = sourceDocs[0];

  // More than one source: the trigger shows the first name + "+N". Hovering
  // reveals the full list (Register-style muted card); clicking the first name
  // opens that document, and each row in the card opens its own document — all
  // in the side panel at the point it's mentioned.
  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>
        <span className="inline-flex max-w-full items-center gap-1 text-left text-[11px] text-gray-500">
          <SourceDocumentLink
            source={primary}
            task={task}
            slug={slug}
            handlers={handlers}
            showLabel
            hideIcon
            labelClassName="flex min-w-0 items-center text-[11px] text-gray-500 hover:text-gray-700 hover:underline underline-offset-2"
          />
          <span className="shrink-0 text-gray-400">+{sourceDocs.length - 1}</span>
        </span>
      </HoverCardTrigger>
      <HoverCardContent className="w-72 p-0" align="start" onClick={(e) => e.stopPropagation()}>
        <div className="space-y-0.5 p-2">
          <div className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">
            Source documents
          </div>
          {sourceDocs.map((source, i) => {
            const { fileType } = getDocumentIcon(source.document_name || '', false);
            return (
              <div key={source.document_guid ?? i} className="flex items-start gap-1.5">
                <span className="mt-1.5 shrink-0 font-mono text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                  {fileType}
                </span>
                <SourceDocumentLink
                  source={source}
                  task={task}
                  slug={slug}
                  handlers={handlers}
                  showLabel
                  hideIcon
                  labelClassName="flex min-w-0 flex-1 items-center rounded px-1 py-1 text-[11px] text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                />
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const buildSourcesColumn = (
  handlers: Parameters<typeof getTaskColumns>[0],
  slug?: string,
): ColumnDef<Task> => ({
  id: 'source',
  meta: {
    filter: { type: 'multi', label: 'File type', order: 6 },
    // Hide the Sources column on tablet and smaller screens; show from lg up.
    headerClassName: 'hidden lg:table-cell',
    cellClassName: 'hidden lg:table-cell',
  },
  accessorFn: (row) => {
    const sourceDocs = getTaskSourceDocuments(row);
    if (isAutoExtracted(row) && sourceDocs.length > 0) {
      return Array.from(
        new Set(sourceDocs.map((sd) => getDocumentIcon(sd.document_name || '', false).fileType)),
      );
    }
    return [];
  },
  size: 110,
  minSize: 90,
  header: ({ table }) => (
    <ColumnHeaderFilter table={table} columnId="source" className="whitespace-nowrap">
      Sources
    </ColumnHeaderFilter>
  ),
  // The table uses auto layout, where a cell's width follows its content. Source
  // links render the (non-wrapping) filename, so without a definite width cap the
  // column stretches to the longest name. Bounding the content lets the inner
  // `truncate` kick in and keeps the column compact.
  cell: ({ row }) => (
    <div className="max-w-48">
      <SourcesCell task={row.original} slug={slug} handlers={handlers} />
    </div>
  ),
  filterFn: 'arrIncludesSome',
  enableSorting: false,
});

// ============================================================================
// COLUMN BUILDER
// ============================================================================

const isCheckboxColumn = (col: ColumnDef<Task>): boolean => col.id === 'id';
const isSourceColumn = (col: ColumnDef<Task>): boolean => col.id === 'source';
const isDateColumn = (col: ColumnDef<Task>): boolean => (col as { accessorKey?: string }).accessorKey === 'date';
// The Party column is not rendered in the Table — party is shown/edited inline
// beneath the description instead (see buildDescriptionWithParty). It is still
// kept as a filter-only column so the Filters menu can filter by party.
const isPartyColumn = (col: ColumnDef<Task>): boolean => col.id === 'party';
const isDescriptionColumn = (col: ColumnDef<Task>): boolean =>
  (col as { accessorKey?: string }).accessorKey === 'description';

// Duplicate the Party value beneath the description text, reusing the same
// editable PartyCell as the Party column. Wraps the shared description column.
const buildDescriptionWithParty = (
  descColumn: ColumnDef<Task>,
  handlers: Parameters<typeof getTaskColumns>[0],
  // Every known party across the tasks, excluding the "Unknown" placeholder.
  // Precomputed once by the caller so it isn't rebuilt inside every cell.
  uniqueParties: string[],
): ColumnDef<Task> => {
  const baseCell = descColumn.cell;
  return {
    ...descColumn,
    // The table uses auto (content-based) layout, so make Description the column
    // that soaks up the leftover width. Without this, hiding content (e.g. the
    // tag chips) lets the slack inflate other columns like Sources instead.
    meta: {
      ...descColumn.meta,
      headerClassName: cn(descColumn.meta?.headerClassName, 'w-full'),
      cellClassName: cn(descColumn.meta?.cellClassName, 'w-full'),
    },
    cell: (ctx) => {
      const task = ctx.row.original;

      return (
        <div className="min-w-0">
          {baseCell ? flexRender(baseCell, ctx) : null}
          <div className="mt-2 flex items-center">
            <PartyCell task={task} uniqueParties={uniqueParties} onUpdateParty={handlers.onUpdateParty} truncate />
          </div>
        </div>
      );
    },
  };
};

// Restore action for soft-deleted rows. Only rendered when an `onRestore`
// handler is provided (i.e. the Table "Show deleted" view).
const buildRestoreColumn = (
  onRestore: NonNullable<Parameters<typeof getTaskColumns>[0]['onRestore']>,
): ColumnDef<Task> => ({
  id: 'restore',
  size: 90,
  header: () => null,
  cell: ({ row }) => {
    const task = row.original;
    if (!task.is_deleted) return null;
    const syntheticId = task.synthetic_deadline_id;
    if (!syntheticId) return null;
    return (
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1"
        onClick={(e) => {
          e.stopPropagation();
          onRestore(syntheticId);
        }}
      >
        <RotateCcw className="h-3.5 w-3.5" />
        Restore
      </Button>
    );
  },
  enableSorting: false,
});

/**
 * Builds the "Table" columns by reusing `getTaskColumns` and swapping the
 * leftmost file-icon column + standalone date column for a single date-first
 * column. Signature mirrors `getTaskColumns`.
 */
export const getTableColumns = (
  handlers: Parameters<typeof getTaskColumns>[0],
  allTasks: Parameters<typeof getTaskColumns>[1],
  activeFiltersRef?: Parameters<typeof getTaskColumns>[2],
  slug?: Parameters<typeof getTaskColumns>[3],
  hideFilters?: Parameters<typeof getTaskColumns>[4],
  members?: Parameters<typeof getTaskColumns>[5],
  litigationTagLabels?: Parameters<typeof getTaskColumns>[6],
  tagVocabulary?: Parameters<typeof getTaskColumns>[7],
  outlook?: TableOutlookOptions,
): ColumnDef<Task>[] => {
  const base = getTaskColumns(
    handlers,
    allTasks,
    activeFiltersRef,
    slug,
    hideFilters,
    members,
    litigationTagLabels,
    tagVocabulary,
  );

  // Precompute once (not per cell/row): the sorted party option list for the
  // inline PartyCell. Previously recomputed inside every rendered cell.
  const uniqueParties = Array.from(
    new Set(allTasks.map((t) => t.party).filter((p): p is string => !!p && p !== 'Unknown')),
  ).sort();

  const checkboxColumn = base.find(isCheckboxColumn);
  const remaining = base
    .filter(
      (col) =>
        !isCheckboxColumn(col) && !isSourceColumn(col) && !isDateColumn(col),
    )
    .map((col) => {
      if (isDescriptionColumn(col)) return buildDescriptionWithParty(col, handlers, uniqueParties);
      if (isPartyColumn(col)) {
        // Keep the party filter (and its `meta.filter`) but don't render a
        // second Party cell — the description column already shows it inline.
        return {
          ...col,
          header: () => null,
          cell: () => null,
          enableHiding: true,
          enableSorting: false,
        } as ColumnDef<Task>;
      }
      return col;
    });
  const dateColumn = buildDateColumn(handlers, activeFiltersRef);
  const outlookColumn =
    outlook?.calendarAvailable && outlook.availableCalendars.length > 0
      ? buildOutlookColumn(outlook)
      : null;
  const sourcesColumn = buildSourcesColumn(handlers, slug);
  const restoreColumn = handlers.onRestore ? buildRestoreColumn(handlers.onRestore) : null;

  // Insert Sources after the Status column (mirrors the Register's layout);
  // fall back to appending if Status isn't found.
  const isStatusColumn = (col: ColumnDef<Task>): boolean =>
    (col as { accessorKey?: string }).accessorKey === 'state';
  const statusIdx = remaining.findIndex(isStatusColumn);
  const ordered = [...remaining];
  if (statusIdx >= 0) {
    ordered.splice(statusIdx + 1, 0, sourcesColumn);
  } else {
    ordered.push(sourcesColumn);
  }

  return [checkboxColumn, dateColumn, ...ordered, restoreColumn, outlookColumn]
    .filter((col): col is ColumnDef<Task> => Boolean(col))
    .map((col) => ({
      ...col,
      meta: {
        ...col.meta,
        cellClassName: cn(col.meta?.cellClassName, 'py-3.5'),
      },
    }));
};
